import React from "react";

const AboutPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700">
        <div className="p-6 border-b border-zinc-200 dark:border-zinc-700">
          <h1 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">
            About Clozeon
          </h1>
        </div>

        <div className="p-8 space-y-8">
          {/* Mission Section */}
          <div>
            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
              Our Mission
            </h2>
            <div className="prose prose-gray dark:prose-invert max-w-none">
              <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                Clozeon is designed to help students and teachers of the Word
                become better readers of the Greek New Testament through
                actively engaging with the Biblical text. We believe that all
                pastors and teachers should be able to engage with the Biblical
                languages in order to stand on the solid ground of Scripture. We
                plan to keep Clozeon free for anyone to use. Planned updates
                include adding support for the Septuagint in Koine Greek, as
                well as other English translations. Adding support for the
                Hebrew Bible is a long-term goal. Your financial support helps
                us to prioritize these updates.
              </p>
            </div>
          </div>

          {/* Mission Section */}
          <div>
            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
              How do you provide this application for free?
            </h2>
            <div className="prose prose-gray dark:prose-invert max-w-none">
              <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                Ironroot Labs LLC is a for-profit company that provides access
                to this application for free, though Clozeon relies on "pay what
                you want" support to cover infrastructure costs and ongoing
                development. If you would like to support the project
                financially, see the section below.
              </p>
              <blockquote className="mt-6 border-l-4 border-teal-500 dark:border-teal-400 pl-6 py-2 bg-zinc-50 dark:bg-zinc-700/30 rounded-r-lg">
                <p className="text-lg italic text-zinc-700 dark:text-zinc-300 mb-2">
                  "Freely you received; freely give."
                </p>
                <cite className="text-sm text-zinc-500 dark:text-zinc-400 not-italic">
                  — Matthew 10:8 (Berean Literal Bible)
                </cite>
              </blockquote>
            </div>
          </div>

          {/* Donations Section */}
          <div>
            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-6">
              Support Development
            </h2>
            <div className="space-y-6">
              <p className="text-zinc-600 dark:text-zinc-300">
                If you find the app useful in your study of Koine Greek, please
                consider supporting the project financially. If you would like
                to support the project on a one-time or recurring basis, you can
                use the button below. We use Buy Me a Coffee as a platform to
                receive the "pay what you want" support.
              </p>

              {/* Support Development Button */}
              <div className="flex justify-center">
                <a
                  href="https://www.buymeacoffee.com/ironrootlabs"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                    alt="Buy Me A Coffee"
                    style={{ height: "60px", width: "217px" }}
                  />
                </a>
              </div>
            </div>
          </div>

          {/* Why don't you? Section */}
          <div>
            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
              Why use public domain texts?
            </h2>
            <div className="prose prose-gray dark:prose-invert max-w-none">
              <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                Rather than licensing from publishers, we use Biblical texts
                from sources in the public domain, in order to make the project
                free to use and accessible to anyone with an internet
                connection. We may add support for other English translations in
                the future, such as the ESV, NASB, NIV, etc, pending licensing
                agreements. See the attribution section below for more details
                on our current textual sources.
              </p>
            </div>
          </div>

          {/* Why Modern Greek Pronunciation Section */}
          <div>
            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
              Why Modern Greek Pronunciation?
            </h2>
            <div className="prose prose-gray dark:prose-invert max-w-none">
              <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                The OpenAI Text-to-Speech API does not support Erasmian or
                Reconstructed Koine pronunciations. Modern Greek is the closest
                available option for hearing Biblical Greek text spoken. While
                this pronunciation differs from traditional Koine Greek study
                methods, it provides an accessible way to hear the text and
                become familiar with Greek phonetics.
              </p>
            </div>
          </div>

          {/* Why don't you? Section */}
          <div>
            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
              Why do users need to create an account?
            </h2>
            <div className="prose prose-gray dark:prose-invert max-w-none">
              <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                Your user account is used to store your custom collections and
                track your progress. We will never share or sell your data.
              </p>
            </div>
          </div>

          {/* Keyboard Shortcuts Section */}
          <div>
            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
              Keyboard Shortcuts
            </h2>
            <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed mb-6">
              Clozeon supports keyboard shortcuts to enhance your learning
              experience. Use these shortcuts to navigate through exercises
              without needing to use your mouse.
            </p>
            <div className="space-y-6">
              <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-700">
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-4">
                  Cloze Practice
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-zinc-200 dark:border-zinc-700">
                    <span className="text-zinc-700 dark:text-zinc-300">
                      Play Greek audio
                    </span>
                    <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                      I
                    </kbd>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-zinc-200 dark:border-zinc-700">
                    <span className="text-zinc-700 dark:text-zinc-300">
                      Navigate down / next option
                    </span>
                    <div className="flex gap-2">
                      <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                        J
                      </kbd>
                      <span className="text-zinc-500 dark:text-zinc-400">
                        or
                      </span>
                      <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                        ↓
                      </kbd>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-zinc-200 dark:border-zinc-700">
                    <span className="text-zinc-700 dark:text-zinc-300">
                      Navigate up / previous option
                    </span>
                    <div className="flex gap-2">
                      <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                        K
                      </kbd>
                      <span className="text-zinc-500 dark:text-zinc-400">
                        or
                      </span>
                      <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                        ↑
                      </kbd>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-zinc-700 dark:text-zinc-300">
                      Select answer / Continue
                    </span>
                    <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                      Enter
                    </kbd>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-700">
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-4">
                  Verb Parsing Practice
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-zinc-200 dark:border-zinc-700">
                    <span className="text-zinc-700 dark:text-zinc-300">
                      Play Greek audio
                    </span>
                    <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                      I
                    </kbd>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-zinc-200 dark:border-zinc-700">
                    <span className="text-zinc-700 dark:text-zinc-300">
                      Toggle context visibility
                    </span>
                    <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                      O
                    </kbd>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-zinc-200 dark:border-zinc-700">
                    <span className="text-zinc-700 dark:text-zinc-300">
                      Navigate down / next option
                    </span>
                    <div className="flex gap-2">
                      <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                        J
                      </kbd>
                      <span className="text-zinc-500 dark:text-zinc-400">
                        or
                      </span>
                      <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                        ↓
                      </kbd>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-zinc-200 dark:border-zinc-700">
                    <span className="text-zinc-700 dark:text-zinc-300">
                      Navigate up / previous option
                    </span>
                    <div className="flex gap-2">
                      <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                        K
                      </kbd>
                      <span className="text-zinc-500 dark:text-zinc-400">
                        or
                      </span>
                      <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                        ↑
                      </kbd>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-zinc-700 dark:text-zinc-300">
                      Select answer / Continue
                    </span>
                    <kbd className="px-3 py-1 bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200 rounded font-mono text-xs">
                      Enter
                    </kbd>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Credits & Attribution Section */}
          <div>
            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-6">
              Attribution
            </h2>
            <div className="space-y-6">
              <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-700">
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-3">
                  English New Testament Text
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed">
                  Berean Literal Bible (BLB) © 2016, 2020 by Bible Hub.{" "}
                  <a
                    href="https://literalbible.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 underline"
                  >
                    https://literalbible.com
                  </a>
                </p>
              </div>

              <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-700">
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-3">
                  Greek New Testament Text
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed">
                  SBL Greek New Testament (SBLGNT), edited by Michael W. Holmes.
                  © 2010 Society of Biblical Literature and Logos Bible
                  Software. Licensed under CC BY 4.0.
                </p>
              </div>

              <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-700">
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-3">
                  Greek Morphological Tagging
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed">
                  Tauber, J. K., ed. (2017) MorphGNT: SBLGNT Edition. Version
                  6.12 [Data set].{" "}
                  <a
                    href="https://github.com/morphgnt/sblgnt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 underline"
                  >
                    https://github.com/morphgnt/sblgnt
                  </a>{" "}
                  DOI:{" "}
                  <a
                    href="https://doi.org/10.5281/zenodo.376200"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 underline"
                  >
                    10.5281/zenodo.376200
                  </a>
                  . Made available under CC BY 3.0.
                </p>
              </div>

              <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-700">
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-3">
                  Text-to-Speech
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400">
                  AI Speech feature is generated via the OpenAI API. All Greek
                  voices use a Modern Greek accent. This feature is
                  experimental. AI may make pronunciation mistakes.
                </p>
              </div>

              <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-700">
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-3">
                  Sound Effects
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed">
                  Sound effects were created by ViRiX Dreamcore (David McKee).
                  <br />
                  <a
                    href="https://soundcloud.com/virix"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 underline"
                  >
                    https://soundcloud.com/virix
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
